import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  limit,
  startAfter,
  writeBatch,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

/**
 * Payment data model
 */
export const Payment = {
  id: "",
  ownerId: "", // Property owner ID
  tenantId: "", // Tenant ID
  unitId: "", // Unit ID
  propertyId: "", // Property ID
  amount: 0, // Payment amount
  type: "rent", // 'rent', 'late_fee', 'security_deposit', 'utility', 'other'
  status: "pending", // 'pending', 'processing', 'paid', 'overdue', 'failed', 'refunded', 'cancelled', ...
  dueDate: null, // When payment is due
  paidDate: null, // When payment was actually paid
  lateFeeAmount: 0, // Late fee if applicable
  method: "ach", // 'ach', 'credit_card', 'check', 'cash', 'stripe', 'paypal'
  transactionId: "", // External transaction ID
  notes: "", // Additional notes
  recurring: false, // Is this a recurring payment
  recurringId: "", // ID of recurring payment if applicable
  isOneOff: false, // Is this a one-off payment (not part of recurring schedule)
  createdAt: null,
  updatedAt: null,
};

/**
 * Rent Schedule data model
 */
export const RentSchedule = {
  id: "",
  ownerId: "",
  unitId: "",
  tenantId: "",
  monthlyAmount: 0,
  dueDay: 1, // Day of month when rent is due
  startDate: null,
  endDate: null,
  isActive: true,
  lateFeeAmount: 0,
  gracePeriod: 3, // Days after due date before late fee
  autoGenerate: true, // Auto-generate rent payments
  createdAt: null,
  updatedAt: null,
};

/**
 * Create a new rent payment
 */
export const createRentPayment = async (paymentData) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const payment = {
      ...paymentData,
      ownerId: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Remove undefined values to prevent Firebase errors
    const cleanedPayment = Object.fromEntries(
      Object.entries(payment).filter(([_, value]) => value !== undefined)
    );

    const docRef = await addDoc(collection(db, "rentPayments"), cleanedPayment);
    return { success: true, id: docRef.id, ...cleanedPayment };
  } catch (error) {
    console.error("Error creating rent payment:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a specific rent payment
 */
export const getRentPayment = async (paymentId) => {
  try {
    const docRef = doc(db, "rentPayments", paymentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    }
    return { success: false, error: "Payment not found" };
  } catch (error) {
    console.error("Error getting rent payment:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to rent payment document changes
 */
export const subscribeRentPayment = (paymentId, callback) => {
  const ref = doc(db, "rentPayments", paymentId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    } else {
      callback(null);
    }
  });
};

/**
 * Update a rent payment
 */
export const updateRentPayment = async (paymentId, updates) => {
  try {
    const docRef = doc(db, "rentPayments", paymentId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating rent payment:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a rent payment
 */
export const deleteRentPayment = async (paymentId) => {
  try {
    const docRef = doc(db, "rentPayments", paymentId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    console.error("Error deleting rent payment:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all payments for a specific tenant
 */
export const getTenantPayments = async (tenantId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "rentPayments"),
      where("ownerId", "==", user.uid),
      where("tenantId", "==", tenantId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const payments = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return { success: true, payments };
  } catch (error) {
    console.error("Error getting tenant payments:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all payments for a specific unit
 */
export const getUnitPayments = async (unitId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "rentPayments"),
      where("ownerId", "==", user.uid),
      where("unitId", "==", unitId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    const payments = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return { success: true, payments };
  } catch (error) {
    console.error("Error getting unit payments:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all payments for the current owner with optional filters
 */
export const getPaymentsByOwner = async (filters = {}) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    console.log("[getPaymentsByOwner] Current user UID:", user.uid);
    console.log("[getPaymentsByOwner] Filters:", filters);

    let q = query(
      collection(db, "rentPayments"),
      where("ownerId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    // Apply additional filters
    if (filters.status && filters.status !== "all") {
      q = query(q, where("status", "==", filters.status));
    }
    if (filters.unitId && filters.unitId !== "all") {
      q = query(q, where("unitId", "==", filters.unitId));
    }
    if (filters.startDate && filters.endDate) {
      q = query(
        q,
        where("dueDate", ">=", filters.startDate),
        where("dueDate", "<=", filters.endDate)
      );
    }

    const querySnapshot = await getDocs(q);
    console.log(
      "[getPaymentsByOwner] Query snapshot size:",
      querySnapshot.size
    );

    const payments = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log("[getPaymentsByOwner] Found payments:", payments);

    return { success: true, payments };
  } catch (error) {
    console.error("Error getting owner payments:", error);
    return { success: false, error: error.message };
  }
};

/**
 * TEMPORARY: Get all payments in the collection (for debugging)
 */
export const getAllPayments = async () => {
  try {
    console.log("[getAllPayments] Fetching all payments...");

    const q = query(
      collection(db, "rentPayments"),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    console.log(
      "[getAllPayments] Total payments in collection:",
      querySnapshot.size
    );

    const payments = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log("[getAllPayments] All payments:", payments);

    return { success: true, payments };
  } catch (error) {
    console.error("Error getting all payments:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get payment statistics for the current owner
 */
export const getPaymentStats = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "rentPayments"),
      where("ownerId", "==", user.uid),
      where("type", "==", "rent")
    );

    const querySnapshot = await getDocs(q);
    const payments = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    const stats = {
      totalRent: 0,
      collectedRent: 0,
      pendingRent: 0,
      processingRent: 0,
      overdueRent: 0,
      collectionRate: 0,
      totalUnits: 0,
      occupiedUnits: 0,
    };

    // Calculate statistics
    console.log("getPaymentStats: Processing payments:", {
      totalPayments: payments.length,
      currentMonth: currentMonth.toISOString(),
      nextMonth: nextMonth.toISOString(),
      timestamp: new Date().toISOString(),
    });

    payments.forEach((payment, index) => {
      const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
      const isCurrentMonth = dueDate >= currentMonth && dueDate < nextMonth;

      // Log all payments for debugging
      console.log(`getPaymentStats: Payment ${index + 1}:`, {
        id: payment.id,
        amount: payment.amount,
        status: payment.status,
        dueDate: dueDate?.toISOString?.() || new Date(dueDate).toISOString(),
        isCurrentMonth,
        tenantId: payment.tenantId,
        tenantName: payment.tenantName,
        unitName: payment.unitName,
        currentMonthStart: currentMonth.toISOString(),
        nextMonthStart: nextMonth.toISOString(),
      });

      if (isCurrentMonth) {
        // Only process current month payments
        console.log(`  → INCLUDED in totalRent (+${payment.amount})`);
        stats.totalRent += payment.amount || 0;
        const status = (payment.status || "").toLowerCase();
        if (status === "paid" || status === "succeeded") {
          stats.collectedRent += payment.amount || 0;
        } else if (status === "processing") {
          stats.processingRent += payment.amount || 0;
        } else if (status === "pending") {
          stats.pendingRent += payment.amount || 0;
        } else if (status === "overdue") {
          stats.overdueRent += payment.amount || 0;
        }
      } else {
        console.log(`  → EXCLUDED (dueDate outside current month)`);
      }
    });

    console.log("getPaymentStats: Final stats:", stats);

    stats.collectionRate =
      stats.totalRent > 0
        ? Math.round((stats.collectedRent / stats.totalRent) * 100)
        : 0;

    return { success: true, stats };
  } catch (error) {
    console.error("Error getting payment stats:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a rent schedule
 */
export const createRentSchedule = async (scheduleData) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const schedule = {
      ...scheduleData,
      ownerId: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, "rentSchedules"), schedule);
    return { success: true, id: docRef.id, ...schedule };
  } catch (error) {
    console.error("Error creating rent schedule:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get rent schedule for a unit
 */
export const getUnitRentSchedule = async (unitId) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, "rentSchedules"),
      where("ownerId", "==", user.uid),
      where("unitId", "==", unitId),
      where("isActive", "==", true)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.docs.length > 0) {
      return {
        success: true,
        data: { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() },
      };
    }
    return { success: false, error: "No active rent schedule found" };
  } catch (error) {
    console.error("Error getting unit rent schedule:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate monthly rent payments for all active units
 */
export const generateMonthlyPayments = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfNextTwoMonths = new Date(
      now.getFullYear(),
      now.getMonth() + 2,
      1
    );
    const startTimestamp = Timestamp.fromDate(startOfMonth);
    const endTimestamp = Timestamp.fromDate(startOfNextTwoMonths);

    // Track existing payments for current and next month to avoid duplicates
    const existingSnapshot = await getDocs(
      query(
        collection(db, "rentPayments"),
        where("ownerId", "==", user.uid),
        where("dueDate", ">=", startTimestamp),
        where("dueDate", "<", endTimestamp)
      )
    );

    // Create a set of existing payment keys (tenantId_unitId_dueDate) to check for duplicates
    const existingKeys = new Set();
    existingSnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.tenantId && data.dueDate) {
        const dueDate = data.dueDate.toDate
          ? data.dueDate.toDate()
          : new Date(data.dueDate);
        const dateKey = `${dueDate.getFullYear()}-${dueDate.getMonth()}-${dueDate.getDate()}`;
        existingKeys.add(`${data.tenantId}_${data.unitId || ""}_${dateKey}`);
      }
    });

    // Prefer rent schedules if they exist
    const schedulesSnapshot = await getDocs(
      query(
        collection(db, "rentSchedules"),
        where("ownerId", "==", user.uid),
        where("isActive", "==", true)
      )
    );

    let targets = schedulesSnapshot.docs.map((docSnap) => ({
      type: "schedule",
      id: docSnap.id,
      ...docSnap.data(),
    }));

    // If no schedules, fall back to active tenants with rent info
    if (targets.length === 0) {
      const tenantsSnapshot = await getDocs(
        query(collection(db, "tenants"), where("ownerId", "==", user.uid))
      );

      targets = tenantsSnapshot.docs
        .map((docSnap) => ({
          type: "tenant",
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter(
          (tenant) =>
            (tenant.monthlyRent || 0) > 0 &&
            tenant.status !== "inactive" &&
            tenant.status !== "moved_out"
        );
    }

    if (targets.length === 0) {
      return {
        success: false,
        error: "No rent schedules or active tenants found.",
      };
    }

    const batch = writeBatch(db);
    let generatedCount = 0;

    targets.forEach((target) => {
      const tenantId = target.tenantId || target.id;
      const unitId = target.unitId || target.unitNumber || "";

      const rawDueDay = Number(target.dueDay || target.rentDueDay || 1);
      const normalizedDueDay = Number.isFinite(rawDueDay)
        ? Math.min(Math.max(rawDueDay, 1), 31)
        : 1;

      // Calculate the next upcoming payment date
      // Start with the current month's due date
      let dueDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        normalizedDueDay
      );
      dueDate.setHours(0, 0, 0, 0);

      // If the due date has already passed this month, move to next month
      if (dueDate < now) {
        dueDate = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          normalizedDueDay
        );
        dueDate.setHours(0, 0, 0, 0);
      }

      // Check if a payment already exists for this calculated due date
      const dateKey = `${dueDate.getFullYear()}-${dueDate.getMonth()}-${dueDate.getDate()}`;
      const key = `${tenantId}_${unitId || ""}_${dateKey}`;

      if (existingKeys.has(key)) {
        return;
      }

      const dueDateTimestamp = Timestamp.fromDate(dueDate);

      const paymentData = {
        ownerId: user.uid,
        tenantId,
        tenantName:
          target.tenantName ||
          `${target.firstName || ""} ${target.lastName || ""}`.trim() ||
          "Tenant",
        tenantEmail: target.email || "",
        unitId: unitId || null,
        amount: target.monthlyAmount || target.monthlyRent || 0,
        type: "rent",
        status: "pending",
        dueDate: dueDateTimestamp,
        lateFeeAmount: target.lateFeeAmount || 0,
        recurring: true,
        recurringId: target.id,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const paymentRef = doc(collection(db, "rentPayments"));
      batch.set(paymentRef, paymentData);
      existingKeys.add(key);
      generatedCount += 1;
    });

    if (generatedCount === 0) {
      return {
        success: false,
        error: "No new payments to generate for this month.",
      };
    }

    await batch.commit();
    return { success: true, generated: generatedCount };
  } catch (error) {
    console.error("Error generating monthly payments:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark payment as paid
 */
export const markPaymentAsPaid = async (
  paymentId,
  paymentMethod = "manual",
  transactionId = ""
) => {
  try {
    const updates = {
      status: "paid",
      paidDate: Timestamp.now(),
      method: paymentMethod,
      transactionId: transactionId,
      updatedAt: Timestamp.now(),
    };

    return await updateRentPayment(paymentId, updates);
  } catch (error) {
    console.error("Error marking payment as paid:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark payment as overdue
 */
export const markPaymentAsOverdue = async (paymentId) => {
  try {
    const updates = {
      status: "overdue",
      updatedAt: Timestamp.now(),
    };

    return await updateRentPayment(paymentId, updates);
  } catch (error) {
    console.error("Error marking payment as overdue:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark payment as pending
 */
export const markPaymentAsPending = async (paymentId) => {
  try {
    const updates = {
      status: "pending",
      paidDate: null,
      method: null,
      transactionId: null,
      updatedAt: Timestamp.now(),
    };

    return await updateRentPayment(paymentId, updates);
  } catch (error) {
    console.error("Error marking payment as pending:", error);
    return { success: false, error: error.message };
  }
};
